/**
 * This file is a partial re-implementation of the disable-devtool package's initialization
 * The original package cannot be tree-shaken and implements a lot of stuff we need to tweak ourselves
 * We just want devtools detection and not bundle anything else
 */

import { originalConsoleMethods } from "@sentry/core";
import { captureMessage } from "@sentry/browser";

/* the utils must be evaluated before the detectors (same order as the
 * package's src/main.ts): interval.ts and detector.ts import each other,
 * and the cycle only resolves in this direction (the reverse throws
 * "Cannot access 'Detector' before initialization") */
import { initLogs } from "disable-devtool/src/utils/log";
import { initInterval } from "disable-devtool/src/utils/interval";
import { initIS } from "disable-devtool/src/utils/util";
import { mergeConfig } from "disable-devtool/src/utils/config";

// use only those detectors
import RegToStringDetector from "disable-devtool/src/detector/sub-detector/reg-to-string";
import DefineIdDetector from "disable-devtool/src/detector/sub-detector/define-id";
import SizeDetector from "disable-devtool/src/detector/sub-detector/size";
import DateToStringDetector from "disable-devtool/src/detector/sub-detector/date-to-string";
import FuncToStringDetector from "disable-devtool/src/detector/sub-detector/func-to-string";
import DebuggerDetector from "disable-devtool/src/detector/sub-detector/debugger";
import DebugLibDetector from "disable-devtool/src/detector/sub-detector/debug-lib";

// The PerformanceDetector generates too much noise in console and is not reliable enough to be used in production
//import PerformanceDetector from 'disable-devtool/src/detector/sub-detector/performance';

import { toHex } from "../shared-utils/hex";
import { sessionNeutral } from "../shared-utils/sentry";

/* sha-256 of the bypass key: opening the reader with ?ddtk=<key> disables the
 * detection (the key itself must not appear in the code) */
const bypassKeyHash =
  "f476339883838f31d41dbd3c4a5bb2c0cade23355391b3ecb7fc17a189ac01c4";
const bypassParamName = "ddtk";

/* our own subset of disable-devtool's init (see the package's src/main.ts):
 * devtools-open detection only, none of the automated behaviors
 * (key/menu/copy blocking, page closing/rewriting, built-in md5 token).
 * kept separate from the ContentProtection class so that disable-devtool
 * doesn't get bundled into the epub-iframe injected script */
export function installDevtoolProtection(
  onDevtoolOpen: (type: number, seenTypes: number[]) => void,
  onDevtoolClose?: () => void
): void {
  if (import.meta.env.DEV) return;

  void install(onDevtoolOpen, onDevtoolClose);
}

async function install(
  onDevtoolOpen: (type: number, seenTypes: number[]) => void,
  onDevtoolClose?: () => void
): Promise<void> {
  if (await isBypassed()) {
    captureMessage("Devtools detection bypassed via query parameter", {
      level: "info",
      ...sessionNeutral,
    });
    return;
  }

  initIS();
  // the toString/getter detectors infer devtools from a side effect of the
  // console rendering a logged object; Sentry's console breadcrumb integration
  // eagerly serializes those args and would trip them with devtools closed.
  // feed the detectors the pre-instrumentation console so only real devtools
  // rendering fires them. originalConsoleMethods is populated per-level by
  // Sentry's instrumentation, so any missing level falls back to window.console.
  initLogs(originalConsoleMethods);

  // ondevtoolopen fires on every interval tick while devtools stays open, and
  // multiple detector types can trigger. forward each type only once per open
  // session so the caller sees a single "opened" per type per open->closed->open cycle.
  const seenTypes = new Set<number>();
  mergeConfig({
    ondevtoolopen: (type: number) => {
      if (seenTypes.has(type)) return;
      seenTypes.add(type);
      onDevtoolOpen(type, Array.from(seenTypes.values()));
    },
    // close detection needs the interval to keep running after a trigger
    ondevtoolclose: () => {
      seenTypes.clear();
      onDevtoolClose?.();
    },
    clearIntervalWhenDevOpenTrigger: false,
    clearLog: false,
  });
  initInterval({ isSuspend: false });

  // initialize only the detectors we want to use
  [
    RegToStringDetector,
    DefineIdDetector,
    SizeDetector,
    DateToStringDetector,
    FuncToStringDetector,
    DebuggerDetector,
    DebugLibDetector,
  ].forEach((detector) => new detector());
}

async function isBypassed(): Promise<boolean> {
  const key = new URL(window.location.href).searchParams.get(bypassParamName);
  if (!key) {
    return false;
  }

  try {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(key)
    );
    return toHex(digest) === bypassKeyHash;
  } catch {
    // fail closed: an unavailable SubtleCrypto never disables the detection
    return false;
  }
}
